import type {
  ApiDashboardElement,
  FixedTimeRangeConfig,
  IntervalJSON,
  SourceMap
} from '@highimpact-frontend/api-types';

import { getSourceLabelFromMap } from '../api/sourceLabelKeys';
import {
  getMeasurePointPathsFromElement,
  resolvePrettyNamesForHydration,
} from '../state/hydrationSourceLabels';

function getElementIntervals(element: ApiDashboardElement): IntervalJSON[] {
  const properties = element.properties as { intervals?: IntervalJSON[] };
  return properties?.intervals ?? [];
}

type DataSourceToAxisEntry = {
  readonly axisName: string;
  readonly unit: string;
  readonly axis_type: string;
  readonly start?: number;
  readonly end?: number;
};

type ElementWithAxisMap = ApiDashboardElement & {
  readonly properties: {
    readonly data_source_to_axis?: Record<number, DataSourceToAxisEntry>;
    readonly intervals?: IntervalJSON[];
  };
};

export interface ElementMeasureColumn {
  readonly index: number;
  readonly name: string;
  readonly axisGroupKey: string;
}

export interface ElementYAxisGroup {
  readonly axisKey: string;
  readonly axisName: string;
  readonly unit: string;
  readonly sourceIndices: readonly number[];
}

function readDataSourceToAxis(
  element: ApiDashboardElement
): Record<number, DataSourceToAxisEntry> {
  const properties = (element as ElementWithAxisMap).properties;
  return properties?.data_source_to_axis ?? {};
}

function buildAxisGroupKey(axis: DataSourceToAxisEntry): string {
  return `${axis.axisName}-${axis.unit}-${axis.axis_type}`;
}

/** Human-readable measure column names aligned with data_source indices. */
export function resolveElementMeasureColumnNames(
  element: ApiDashboardElement,
  sourceLabelMap: SourceMap
): string[] {
  const paths = getMeasurePointPathsFromElement(element);

  if (paths.length === 0) {
    return [];
  }

  const plotType = (element.properties as { plot_type?: string })?.plot_type;

  if (plotType === 'bar') {
    return paths.map(
      (path) =>
        getSourceLabelFromMap(sourceLabelMap, path) ??
        `Point ${path.measure_point_id} (${path.measure_type_id})`
    );
  }

  if (Object.keys(sourceLabelMap).length > 0) {
    return paths.map(
      (path) =>
        getSourceLabelFromMap(sourceLabelMap, path) ??
        `Point ${path.measure_point_id} (${path.measure_type_id})`
    );
  }

  return resolvePrettyNamesForHydration(element, sourceLabelMap).map(
    ([, name]) => name
  );
}

/** Maps each data source index to a unified Y axis group derived from element JSON. */
export function groupElementYAxes(
  element: ApiDashboardElement
): ElementYAxisGroup[] {
  const axisByIndex = readDataSourceToAxis(element);
  const grouped = new Map<string, ElementYAxisGroup>();

  for (const [indexStr, axisData] of Object.entries(axisByIndex)) {
    const index = Number(indexStr);
    if (!Number.isFinite(index) || axisData.axis_type !== 'y') {
      continue;
    }

    const groupKey = buildAxisGroupKey(axisData);
    const existing = grouped.get(groupKey);
    if (existing) {
      grouped.set(groupKey, {
        ...existing,
        sourceIndices: [...existing.sourceIndices, index],
      });
      continue;
    }

    grouped.set(groupKey, {
      axisKey: `yaxis${grouped.size + 1}`,
      axisName: axisData.axisName,
      unit: axisData.unit,
      sourceIndices: [index],
    });
  }

  return [...grouped.values()];
}

/** Column metadata for bar/histogram widgets. */
export function buildHistogramMeasureColumns(
  element: ApiDashboardElement,
  sourceLabelMap: SourceMap
): ElementMeasureColumn[] {
  const axisByIndex = readDataSourceToAxis(element);
  const names = resolveElementMeasureColumnNames(element, sourceLabelMap);

  return names.map((name, index) => ({
    index,
    name,
    axisGroupKey: buildAxisGroupKey(
      axisByIndex[index] ?? {
        axisName: name,
        unit: '',
        axis_type: 'y',
      }
    ),
  }));
}

/** Interval display names for histogram traces. */
export function resolveElementIntervalNames(
  element: ApiDashboardElement
): string[] {
  const intervals = getElementIntervals(element);

  return intervals.map((interval) => {
    if (interval.name) {
      return interval.name;
    }

    const fixed = interval as FixedTimeRangeConfig;
    if (fixed.startdate && fixed.enddate) {
      const startDate = new Date(fixed.startdate);
      const endDate = new Date(fixed.enddate);
      const options: Intl.DateTimeFormatOptions = {
        month: 'long',
        day: 'numeric',
      };

      if (startDate.getFullYear() === endDate.getFullYear()) {
        if (startDate.getMonth() === endDate.getMonth()) {
          return `${startDate.toLocaleDateString(undefined, options)}–${endDate.getDate()}, ${startDate.getFullYear()}`;
        }
        return `${startDate.toLocaleDateString(undefined, options)} – ${endDate.toLocaleDateString(undefined, options)}, ${startDate.getFullYear()}`;
      }

      return `${startDate.toLocaleDateString(undefined, options)} – ${endDate.toLocaleDateString(undefined, options)}`;
    }

    return 'Intervalle';
  });
}

/** Ombrothermic Y-axis metadata (precipitation + temperature). */
export function buildOmbrothermicAxisGroups(
  element: ApiDashboardElement
): ElementYAxisGroup[] {
  return groupElementYAxes(element).slice(0, 2);
}
